import React, { useCallback, useMemo, useState } from 'react';
import styles from './storedValueReconciliation.module.scss';
import i18n from 'i18n';
import moment from 'moment';
import { DateRangePickerField } from 'components/form/field/DateRangePickerField';
import { CustomField } from 'components/form/field/CustomField';
import { Button } from 'react-bootstrap';
import { DefaultReportManager } from 'core/report/ReportManager';
import { useCallAPI } from 'hooks/useCallAPI';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { ReconciliationReportType } from 'ws/ReportWebService';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { SelectField } from 'components/form/field/SelectField';
import { SelectOptions } from 'components/commonType';
import { Trans } from 'react-i18next';

const defaultReportManager = new DefaultReportManager();
export const MAX_QUERY_DAYS_MONTHLY = 31;
// 對齊 BE StoredValueReconciliationService.MAX_PERIOD_DAYS（含頭尾 92 天）；
// 超過的區間 BE 會直接回 400，這裡先擋住讓使用者看得到原因。
export const MAX_QUERY_DAYS_STANDARD = 92;

// 報表資料的起算日。早於此日的期間「從未被驗證過」—— 歷史漏扣已由弭平補列正確歸期沖平，
// 但 e = 實扣 + 弭平 − 應扣 + freezeNet 裡的 freezeNet（凍結 / 解凍）不落在 a / b / c 任何一項，
// 上版前的期間沒有人核對過那部分，所以預設不開放查詢，避免拿到會誤導的數字。
// 🔴 兩個常數都綁「上版日 2026-08-27」——上版日若改，這裡要跟著改。
// ⚠️ 刻意只擋 FE、BE 不擋：稽核需要時（例如重跑 6/1 ~ 8/31 的基準報表）仍可直接打 API 取得。
export const MIN_QUERY_DATE_STANDARD = '2026-08-27';
// 8/27 上版即落在 8 月內，所以「月」的粒度從 8 月起可查。
export const MIN_QUERY_DATE_MONTHLY = '2026-08-01';

// 起算日與天數上限共用同一個出口 —— 兩者都要「顯示原因 + disable 下載鈕」，
// 而那由 dayRangeError 這一個狀態驅動（見 downloadButton 的 disabled）。
// 天數是含頭尾的（from 取 startOf('day')、to 取 endOf('day')），與 BE 的
// ChronoUnit.DAYS.between(from, to) + 1 同義。
export const getDayRangeErrorMessage = (
  momentFrom: moment.Moment,
  momentTo: moment.Moment,
  maxDays: number,
  minDate?: string
) => {
  if (minDate && momentFrom.isBefore(moment(minDate).startOf('day'))) {
    return i18n.t<string>('report.descriptions.earliestQueryDate', { date: minDate });
  }
  const diffDays = Math.ceil(Math.abs(momentTo.diff(momentFrom)) / (1000 * 60 * 60 * 24));
  return diffDays > maxDays ? i18n.t<string>('campaign.descriptions.moreThanDays', { days: maxDays }) : undefined;
};

export const StoredValueReconciliation: React.FC = () => {

  const [reportType, setReportType] = useState<SelectOptions>();
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [dayRangeError, setDayRangeError] = useState<string | undefined>(undefined);
  const { loading, callAPIs } = useCallAPI();

  const handleOnChangeReportType = useCallback((value: SelectOptions) => {
    setReportType(value);
    const isMonth = value.value === ReconciliationReportType.MONTH;
    const momentFrom = isMonth
      ? moment().subtract(1, 'month').startOf('month')
      : moment().subtract(7, 'days').startOf('day');
    const momentTo = isMonth
      ? moment().subtract(1, 'month').endOf('month')
      : moment().subtract(1, 'days').endOf('day');
    setFrom(momentFrom.format('YYYY-MM-DD'));
    setTo(momentTo.format('YYYY-MM-DD'));
    // 🔴 預設區間也要驗：它是這裡算出來的、不是使用者從 picker 選的，
    // 所以 picker 的 minDate 管不到它。上版初期預設區間會整段落在起算日之前，
    // 少了這一驗就會出現「畫面沒有錯誤、按下下載卻拿到誤導數字」。
    setDayRangeError(getDayRangeErrorMessage(
      momentFrom,
      momentTo,
      isMonth ? MAX_QUERY_DAYS_MONTHLY : MAX_QUERY_DAYS_STANDARD,
      isMonth ? MIN_QUERY_DATE_MONTHLY : MIN_QUERY_DATE_STANDARD
    ));
  }, []);

  const downloadReport = useCallback(() => {
    if (reportType) {
      callAPIs([() => defaultReportManager.downloadStoredValueReconciliationReport(from, to)]);
    }
  }, [callAPIs, reportType, from, to]);

  const renderScheduling = useCallback(() => {
    const endDateMoment = moment(to);
    const startDateMoment = moment(from);
    const days = endDateMoment.diff(startDateMoment, 'days');
    if (days > 0) {
      return (
        <Trans i18nKey='campaign.descriptions.campaignSchedulingDay'>
          Total <span className='text-dark'>{{ days: days + 1 }} days</span>
        </Trans>
      );
    }
  }, [from, to]);

  const reportTypeOptions = useMemo(() => createSelectOptionsFromEnum(ReconciliationReportType, 'report.labels.storedValueReconciliation.'), []);

  const reportTypeDatePickerElementMap: { [key: string | number]: JSX.Element } = useMemo(() => {
    const renderStandardDayRange = () => {
      const setUpStandardDayRange = (startDate: string | undefined, endDate: string | undefined) => {
        const momentFrom = moment(startDate ?? from).startOf('day');
        const momentTo = moment(endDate ?? to).endOf('day');
        if (startDate) {
          setFrom(momentFrom.format('YYYY-MM-DD'));
        }
        if (endDate) {
          setTo(momentTo.format('YYYY-MM-DD'));
        }
        setDayRangeError(getDayRangeErrorMessage(momentFrom, momentTo, MAX_QUERY_DAYS_STANDARD, MIN_QUERY_DATE_STANDARD));
      };
      return (
        <DateRangePickerField
          label={i18n.t<string>('report.labels.reportGran')}
          startDate={from}
          endDate={to}
          minDate={MIN_QUERY_DATE_STANDARD}
          maxDate={moment().endOf('day').subtract(1, 'day').format('YYYY-MM-DD')}
          format={'YYYY-MM-DD'}
          onChange={setUpStandardDayRange}
          showTimePicker={false}
          hint={renderScheduling()}
          error={dayRangeError}
        />
      );
    };
    const renderMonthRange = () => {
      const setUpMonthRange = (startDate: string | undefined, endDate: string | undefined) => {
        if (!startDate || !endDate) {
          return;
        }
        const momentFrom = moment(startDate).startOf('day');
        const momentTo = moment(endDate).endOf('day');
        setFrom(momentFrom.format('YYYY-MM-DD'));
        setTo(momentTo.format('YYYY-MM-DD'));
        setDayRangeError(getDayRangeErrorMessage(momentFrom, momentTo, MAX_QUERY_DAYS_MONTHLY, MIN_QUERY_DATE_MONTHLY));
      };
      return (
        <DateRangePickerField
          label={i18n.t<string>('report.labels.reportGran')}
          startDate={from}
          endDate={to}
          minDate={MIN_QUERY_DATE_MONTHLY}
          maxDate={moment().endOf('day').subtract(1, 'day').format('YYYY-MM-DD')}
          format={'YYYY-MM-DD'}
          onChange={setUpMonthRange}
          showTimePicker={false}
          hint={renderScheduling()}
          error={dayRangeError}
        />
      );
    };
    return {
      [ReconciliationReportType.STANDARD]: renderStandardDayRange(),
      [ReconciliationReportType.MONTH]: renderMonthRange()
    };
  }, [from, to, dayRangeError, renderScheduling]);

  return (
    <div className={styles.container}>
      {loading && <LoadingIndicator/>}
      <div className={styles.titleArea}>
        <div className={styles.title}>
          {i18n.t<string>('report.titles.storedValueReconciliation')}
        </div>
      </div>
      <div className={styles.contentArea}>
        <SelectField
          label={i18n.t<string>('report.labels.reportType')}
          name='reportType'
          options={reportTypeOptions}
          value={reportType}
          onChange={handleOnChangeReportType}
        />
        {reportType && reportTypeDatePickerElementMap[reportType.value]}
        {reportType &&
          <CustomField label='' name='reportDownloadButton'>
            <Button variant='primary' onClick={downloadReport} size='sm' disabled={dayRangeError !== undefined}>
              {i18n.t<string>('report.labels.download')}
            </Button>
          </CustomField>
        }
      </div>
    </div>
  );
};
